import React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import useScrollTrigger from "@mui/material/useScrollTrigger";

import AppMenu from "../side-bar/AppMenu";
import { ClickActionEvent } from "../../util/analytics";
import scrollToSection from "../../util/scrollToSection";
import appMenuItems from "../side-bar/AppMenuItemsList";
import { useLocation, useNavigate } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

// A simplified, robust AppBar implementation.
export default function ButtonAppBar() {
  const location = useLocation();
  const notHomePage = location.pathname !== "/"
  const isResumeBuilder = location.pathname === "/create-resume";
  const navigate = useNavigate();

  const [leftMenu, setLeftMenu] = React.useState(false);
  const [navSelected, setNavSelected] = React.useState(
    location.pathname === "/" ? "home" : location.pathname.slice(1)
  );
  const [anchorMap, setAnchorMap] = React.useState({});
  const sectionSelectionLock = React.useRef(null);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50, // px before color appears
  });

  const showHamburgerMenu = { display: { xs: "block", sm: "none" } };
  const showAppBarNav = {
    mr: 2,
    fontSize: "1rem",
    display: { xs: "none", sm: "inline-flex" },
    borderRadius: 1.5,
    minWidth: "auto",
    px: 1.25,
    py: 0.75,
  };

  const toggleDrawer = (menuState) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ){
      return;
    }
    setLeftMenu(menuState);
  };

  const setNav = (name) => {
    setNavSelected(name);
    const sectionItem = appMenuItems
      .flatMap((item) => item.items || [item])
      .find((item) => item.id === name);
    if (sectionItem && !sectionItem.route) {
      if (sectionSelectionLock.current) clearTimeout(sectionSelectionLock.current);
      sectionSelectionLock.current = setTimeout(() => {
        sectionSelectionLock.current = null;
      }, 1400);
    }
    ClickActionEvent("Menu " + name, "homepage", "app menu clicked");
  };

  // "For every section ID I'm interested in, find that section in the DOM and tell my IntersectionObserver to watch it. When this React effect needs to be replaced or removed, stop the observer from watching everything."
  React.useEffect(() => {
    if (notHomePage) {
      setNavSelected(location.pathname.slice(1));
      return undefined;
    }

    const visibleSections = new Map();
    const sectionIds = appMenuItems
      .flatMap((item) => item.items || [item])
      .filter((item) => !item.route)
      .map((item) => item.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleSections.set(entry.target.id, entry.target);
          } else {
            visibleSections.delete(entry.target.id);
          }
        });

        // Among all currently visible sections, which one is closest to 80px from the top of the viewport?
        const activeSection = [...visibleSections.values()].sort(
          (first, second) =>
            Math.abs(first.getBoundingClientRect().top - 80) -
            Math.abs(second.getBoundingClientRect().top - 80)
        )[0];

        if (activeSection && !sectionSelectionLock.current) {
          setNavSelected(activeSection.id);
        }
      },
      { rootMargin: "-80px 0px -55% 0px", threshold: 0 }
    );

    sectionIds.forEach((sectionId) => {
      const section = document.getElementById(sectionId);
      if (section) observer.observe(section); // Now the browser watches all those sections.
    });

    return () => observer.disconnect(); 
    // Stop observing all elements with this observer.
    // THis is to stop potentially end up with old observers still running.
  }, [location.pathname, notHomePage]);

  const navButtonSx = (name) => ({
    ...showAppBarNav,
    ...(name === "create-resume"
      ? {
          border: "1px solid currentColor",
          borderRadius: 2,
          fontWeight: 700,
          px: 1.25,
          py: 0.5,
          ml: 0.5,
        }
      : {}),
    ...(navSelected === name || appMenuItems.some(
      (item) => item.id === name && item.items?.some((child) => child.id === navSelected)
    )
      ? {
          backgroundColor: "#0F766E",
          color: "#FFFFFF",
          "&:hover": { backgroundColor: "#115E59" },
        }
      : {}),
  });

  const dropdownItemSx = (id) => ({
    ...(navSelected === id
      ? {
          backgroundColor: "#0F766E",
          color: "#FFFFFF",
          fontWeight: 700,
          "&:hover": { backgroundColor: "#115E59" },
        }
      : {}),
  });

  const handleHomeClick = () => {
    setNav("home");
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    navigate("/", { state: { scrollTo: "home" } });
  };

  const openDropdown = (id, anchorElement) =>
    setAnchorMap((m) => ({ ...m, [id]: anchorElement }));
  const closeDropdown = (id, itemName) => {
    setAnchorMap((m) => ({ ...m, [id]: null }));
    // if (itemName) setNavSelected(itemName);
  };

  const renderDropdown = (menu) => {
    const anchorEl = anchorMap[menu.id] || null;
    const onItemClick = (item) => {
      closeDropdown(menu.id, item.name);
      // If the dropdown item has an explicit route, navigate to it directly.
      if (item.route) {
        setNav(item.id);
        navigate(item.route);
        return;
      }
      // Otherwise, if we're not on the homepage, navigate to home and pass the scroll target.
      setNav(item.id);
      if (notHomePage)
        navigate("/", { state: { scrollTo: item.id } });
    };

    return (
      <React.Fragment key={menu.id}>
        <IconButton
          onClick={(e) => {
            openDropdown(menu.id, e.currentTarget);
          }}
          sx={navButtonSx(menu.id)}
          color="inherit"
        >
          {menu.name}
          <ArrowDropDownIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => closeDropdown(menu.id, null)}
        >
          {menu.items.map((it) => {
            // If the item defines a route, always navigate to that route when clicked.
            if (it.route) {
              return (
                <MenuItem
                  key={it.id}
                  sx={dropdownItemSx(it.id)}
                  onClick={() => {
                    closeDropdown(menu.id, it.name);
                    setNav(it.id);
                    navigate(it.route);
                  }}
                >
                  {it.name}
                </MenuItem>
              );
            }

            // Items without a route behave as scroll targets on the homepage.
            if (location.pathname === "/") {
              return (
                <MenuItem key={it.id} sx={dropdownItemSx(it.id)} onClick={() => {
                    closeDropdown(menu.id, it.name);
                    setNav(it.id);
                    scrollToSection(it.id);
                  }}>
                  {it.name}
                </MenuItem>
              );
            }

            // Off-home: navigate to home and request a scroll to the target.
            return (
              <MenuItem key={it.id} sx={dropdownItemSx(it.id)} onClick={() => onItemClick(it)}>
                {it.name}
              </MenuItem>
            );
          })}
        </Menu>
      </React.Fragment>
    );
  };

  const nodes = appMenuItems.filter((item) => item.id !== "home").map((item) => {
    if (item.items) return renderDropdown(item);
    if (item.route) {
      return (
        <IconButton
          key={item.id}
          onClick={() => {
            setNav(item.id);
            navigate(item.route);
          }}
          sx={navButtonSx(item.id)}
          color="inherit"
        >
          {item.name}
        </IconButton>
      );
    }
    if (location.pathname === "/") {
      return (
        <IconButton
          key={item.id}
          onClick={() => {
            setNav(item.id);
            scrollToSection(item.id || "home");
          }}
          sx={navButtonSx(item.id)}
          color="inherit"
        >
          {item.name}
        </IconButton>
      );
    }
    return (
      <IconButton
        key={item.id}
        onClick={() => {
          setNav(item.id);
          navigate("/", { state: { scrollTo: item.id } });
        }}
        sx={navButtonSx(item.id)}
        color="inherit"
      >
        {item.name}
      </IconButton>
    );
  });

  return (
    <div>
      <AppBar
        position="fixed"
        elevation={trigger ? 4 : 0}
        sx={{
          width: { xs: isResumeBuilder ? "auto" : "100%", sm: "100%" },
          right: { xs: isResumeBuilder ? "auto" : 0, sm: 0 },
          backgroundColor: { xs: isResumeBuilder ? "transparent" : (trigger || notHomePage ? "#E8F5F2" : "transparent"), sm: trigger || notHomePage ? "#E8F5F2" : "transparent" },
          color: trigger || notHomePage ? "#061B3A" : "#c4c4b9",
          boxShadow: { xs: isResumeBuilder ? "none" : undefined, sm: undefined },
          transition: "background-color 0.3s ease, box-shadow 0.3s ease",
        }}
      >
        <Toolbar sx={isResumeBuilder ? { minHeight: { xs: "0 !important", sm: 64 }, px: { xs: 0, sm: 3 } } : undefined}>
          <Box
            component="img"
            src="/javedinfinte-logo-128.png"
            alt="javedinfinite"
            onClick={handleHomeClick}
            sx={{
              display: { xs: "none", sm: "block" },
              width: 148,
              height: { xs: 30, sm: 38 },
              objectFit: "contain",
              cursor: "pointer",
              mr: 2,
            }}
          />
          <IconButton
            edge="start"
            onClick={toggleDrawer(true)}
            sx={{
              ...showHamburgerMenu,
              ...(isResumeBuilder ? {
                m: 1,
                width: 40,
                height: 40,
                backgroundColor: "rgba(232, 245, 242, 0.94)",
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.18)",
                "&:hover": { backgroundColor: "#E8F5F2" },
              } : {}),
            }}
            color="inherit"
          >
            <MenuIcon />
          </IconButton>

          {nodes}

          <div style={{ marginLeft: "auto" }} />
        </Toolbar>
      </AppBar>
      <AppMenu toggleDrawer={toggleDrawer} drawerState={leftMenu} />
    </div>
  );
}
